<template>
  <div class="production-line-list">
    <!-- 顶部栏 - 使用TopBar组件 -->
    <top-bar :user-name="userName" class="list-top-bar" />
    
    <div class="list-content">
      <div v-if="loading && productionLines.length === 0" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">加载中...</div>
      </div>
      
      <div v-else-if="error" class="error-state">
        <i class="el-icon-warning"></i>
        <div class="error-text">{{ error }}</div>
      </div>
      
      <div v-else-if="productionLines.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <div class="empty-text">暂无生产线数据</div>
      </div>
      
      <div v-else class="line-grid">
        <div
          v-for="line in productionLines"
          :key="line.worklineCode"
          class="line-card"
          :class="getCardClass(line)"
          @dblclick="selectLine(line)"
        >
          <div class="card-header">
            <div class="line-name">
              <span class="status-dot" :class="getStatusDotClass(line)"></span>
              {{ line.worklineName }}
            </div>
            <div class="line-code">{{ line.worklineCode }}</div>
          </div>
          
          <div class="card-body">
            <div class="info-row">
              <div class="info-label">
                <i class="el-icon-document"></i>
                生产工单
              </div>
              <div class="info-value">{{ line.workOrderNo || '-' }}</div>
            </div>
            
            <div class="info-row progress-row">
              <div class="info-label">
                <i class="el-icon-data-line"></i>
                采集进度
              </div>
              <div class="progress-info">
                <div class="progress-bar-wrapper">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :class="getProgressClass(line.progressPercentage)"
                      :style="{ width: line.progressPercentage + '%' }"
                    ></div>
                  </div>
                  <div class="progress-text">{{ line.progressPercentage }}%</div>
                </div>
                <div class="quantity-text">
                  {{ formatNumber(line.collectedQuantity) }} / {{ formatNumber(line.planQuantity) }}
                </div>
              </div>
            </div>
            
            <div class="info-row">
              <div class="info-label">
                <i class="el-icon-time"></i>
                最后上传
              </div>
              <div class="info-value time-value">{{ formatTime(line.lastUploadTime) }}</div>
            </div>
            
            <div class="info-row alarm-row">
              <div class="info-label">
                <i class="el-icon-bell"></i>
                报警次数
              </div>
              <div class="alarm-badge" :class="{ 'has-alarm': line.alarmCount > 0 }">
                {{ line.alarmCount }}
              </div>
            </div>
          </div>
          
          <div class="card-footer">
            <div class="hint-text">
              <i class="el-icon-mouse"></i>
              双击查看详情
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TopBar from './TopBar.vue'

export default {
  name: 'ProductionLineList',
  components: {
    TopBar
  },
  props: {
    productionLines: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    userName: {
      type: String,
      default: '系统用户'
    }
  },
  methods: {
    selectLine(line) {
      this.$emit('select-line', line.worklineCode)
    },
    
    getCardClass(line) {
      // 根据最后上传时间判断是否在线
      if (!line.lastUploadTime) return 'status-offline'
      
      const lastTime = new Date(line.lastUploadTime).getTime()
      const now = Date.now()
      const diff = now - lastTime
      
      // 超过10分钟未上传视为离线
      if (diff > 600000) return 'status-offline'
      return 'status-online'
    },
    
    getStatusDotClass(line) {
      if (!line.lastUploadTime) return 'dot-offline'
      
      const lastTime = new Date(line.lastUploadTime).getTime()
      const now = Date.now()
      const diff = now - lastTime
      
      // 超过10分钟未上传视为离线
      if (diff > 600000) return 'dot-offline'
      return 'dot-online'
    },
    
    getProgressClass(percentage) {
      if (percentage >= 90) return 'progress-excellent'
      if (percentage >= 70) return 'progress-good'
      if (percentage >= 50) return 'progress-normal'
      return 'progress-low'
    },
    
    formatNumber(num) {
      if (!num && num !== 0) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    formatTime(timeStr) {
      if (!timeStr) return '-'
      
      const date = new Date(timeStr)
      const now = new Date()
      const diff = now - date
      
      // 小于1分钟显示"刚刚"
      if (diff < 60000) return '刚刚'
      
      // 小于1小时显示"X分钟前"
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000)
        return `${minutes}分钟前`
      }
      
      // 同一天显示时分秒
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString('zh-CN', { hour12: false })
      }
      
      // 不同天显示完整日期时间
      return date.toLocaleString('zh-CN', { 
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.production-line-list {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%);
  position: relative;
  
  // 背景网格
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(0, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
  }
}

.list-top-bar {
  flex-shrink: 0;
}

.list-content {
  flex: 1;
  padding: 30px;
  overflow-y: auto;
  position: relative;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.3);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 255, 255, 0.5);
    }
  }
}

.loading-state,
.error-state,
.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 255, 255, 0.1);
  border-top-color: #00ffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

.loading-text {
  margin-top: 20px;
  color: #00ffff;
  font-size: 16px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state,
.empty-state {
  i {
    font-size: 64px;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 20px;
  }
}

.error-text,
.empty-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
}

.line-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 25px;
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.line-card {
  background: rgba(10, 20, 40, 0.7);
  border: 2px solid rgba(0, 255, 255, 0.3);
  border-radius: 10px;
  padding: 20px;
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: all 0.3s ease;
  clip-path: polygon(
    0 12px, 12px 0,
    calc(100% - 12px) 0, 100% 12px,
    100% calc(100% - 12px), calc(100% - 12px) 100%,
    12px 100%, 0 calc(100% - 12px)
  );
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 30px rgba(0, 255, 255, 0.4);
    border-color: rgba(0, 255, 255, 0.6);
  }
  
  &.status-online {
    border-color: rgba(0, 255, 0, 0.5);
    
    &:hover {
      box-shadow: 0 8px 30px rgba(0, 255, 0, 0.4);
    }
  }
  
  &.status-offline {
    border-color: rgba(128, 128, 128, 0.5);
    opacity: 0.7;
    
    &:hover {
      box-shadow: 0 8px 30px rgba(128, 128, 128, 0.3);
    }
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid rgba(0, 255, 255, 0.2);
}

.line-name {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #00ffff;
  font-size: 22px;
  font-weight: bold;
  text-shadow: 0 0 15px rgba(0, 255, 255, 0.6);
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  
  &.dot-online {
    background: #00ff00;
    box-shadow: 0 0 15px rgba(0, 255, 0, 1);
    animation: pulse 2s ease-in-out infinite;
  }
  
  &.dot-offline {
    background: #666;
    box-shadow: 0 0 8px rgba(102, 102, 102, 0.5);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.line-code {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-family: 'Courier New', monospace;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  border-left: 3px solid rgba(0, 255, 255, 0.5);
  
  &.progress-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  &.alarm-row {
    border-left-color: rgba(255, 255, 0, 0.5);
  }
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  
  i {
    color: #00ffff;
    font-size: 16px;
  }
}

.info-value {
  color: #ffffff;
  font-size: 15px;
  font-weight: 500;
  text-align: right;
}

.time-value {
  color: #00ffff;
  font-family: 'Courier New', monospace;
}

.progress-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 20px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(0, 255, 255, 0.3);
}

.progress-fill {
  height: 100%;
  transition: width 0.5s ease;
  border-radius: 10px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  &.progress-excellent {
    background: linear-gradient(90deg, #00ff00, #00cc00);
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
  }
  
  &.progress-good {
    background: linear-gradient(90deg, #00ffff, #00cccc);
    box-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  }
  
  &.progress-normal {
    background: linear-gradient(90deg, #ffff00, #cccc00);
    box-shadow: 0 0 10px rgba(255, 255, 0, 0.5);
  }
  
  &.progress-low {
    background: linear-gradient(90deg, #ff6600, #cc5500);
    box-shadow: 0 0 10px rgba(255, 102, 0, 0.5);
  }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-text {
  color: #00ffff;
  font-size: 16px;
  font-weight: bold;
  min-width: 50px;
  text-align: right;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.6);
}

.quantity-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  text-align: right;
}

.alarm-badge {
  padding: 4px 12px;
  border-radius: 12px;
  background: rgba(128, 128, 128, 0.2);
  border: 1px solid rgba(128, 128, 128, 0.5);
  color: #999;
  font-size: 16px;
  font-weight: bold;
  min-width: 40px;
  text-align: center;
  
  &.has-alarm {
    background: rgba(255, 0, 0, 0.2);
    border-color: rgba(255, 0, 0, 0.5);
    color: #ff4444;
    animation: alarm-pulse 1.5s ease-in-out infinite;
  }
}

@keyframes alarm-pulse {
  0%, 100% {
    box-shadow: 0 0 5px rgba(255, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 0, 0, 0.6);
  }
}

.card-footer {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid rgba(0, 255, 255, 0.2);
}

.hint-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  
  i {
    font-size: 14px;
  }
}
</style>
